import { useState, useEffect, useRef } from 'react';

interface UseScrollVisibilityOptions {
  /**
   * Whether the hook should be active (default: true)
   * Set to false to always show (e.g., on desktop)
   */
  enabled?: boolean;
  /**
   * Scroll threshold for hiding (default: 80)
   * Element hides when scrolling down past this point
   */
  hideThreshold?: number;
  /**
   * Scroll threshold for showing (default: 10)
   * Element shows when near top or scrolling up
   */
  showThreshold?: number;
}

/**
 * Hook to manage element visibility based on scroll direction
 * Shows when scrolling up or near top, hides when scrolling down
 */
export function useScrollVisibility({
  enabled = true,
  hideThreshold = 80,
  showThreshold = 10,
}: UseScrollVisibilityOptions = {}) {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setIsVisible(true);
      return;
    }

    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          // Show when scrolling up or at the top
          if (currentScrollY < lastScrollY.current || currentScrollY < showThreshold) {
            setIsVisible(true);
          }
          // Hide when scrolling down (and not at the very top)
          else if (currentScrollY > lastScrollY.current && currentScrollY > hideThreshold) {
            setIsVisible(false);
          }

          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, hideThreshold, showThreshold]);

  return { isVisible };
}
